import React, { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

const easeInOutCubic = (t) =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

export function NumberTicker({
  value,
  start = true,
  delay = 0,
  duration = 2200,
  decimalPlaces = 0,
  once = true,
  respectReducedMotion = false,
  className,
}) {
  const [displayValue, setDisplayValue] = useState(0);
  const hasAnimatedRef = useRef(false);

  useEffect(() => {
    if (!start) return undefined;
    if (once && hasAnimatedRef.current) return undefined;

    hasAnimatedRef.current = true;

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    if (respectReducedMotion && prefersReducedMotion) {
      setDisplayValue(value);
      return undefined;
    }

    let frameId = 0;
    let timeoutId = 0;

    const run = () => {
      setDisplayValue(0);
      const startTime = performance.now();

      const tick = (now) => {
        const progress = Math.min((now - startTime) / duration, 1);
        const easedProgress = easeInOutCubic(progress);
        setDisplayValue(value * easedProgress);

        if (progress < 1) {
          frameId = window.requestAnimationFrame(tick);
        }
      };

      frameId = window.requestAnimationFrame(tick);
    };

    timeoutId = window.setTimeout(run, delay * 1000);

    return () => {
      window.clearTimeout(timeoutId);
      window.cancelAnimationFrame(frameId);
    };
  }, [start, value, delay, duration, once, respectReducedMotion]);

  const roundedValue =
    decimalPlaces > 0 ? Number(displayValue) : Math.floor(displayValue);

  const formattedValue = Number(roundedValue).toLocaleString(undefined, {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  });

  return <span className={cn("tabular-nums", className)}>{formattedValue}</span>;
}
